import "pages/EstimateViewer/TableOuterBox.css";
import "./SummaryEstimateViewer.css";

import {
	lazy,
	Suspense,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import apiSummaryEstimate from "api/summary_estimate";
import AlertLoading from "components/AlertLoading";
import HeaderVisibleButton from "components/HeaderVisibleButton";
import NoDataBox from "components/NoDataBox";
import StartWorkBox from "components/StartWorkBox";
import Table from "components/Table/Table";
import LabelNormWithUnder from "components/UI/LabelNormWithUnder";
import { LabelTwoVerticalLevel } from "components/UI/labels";
import EstmHeaderTitleButtons from "pages/EstimateViewer/components/EstmHeaderTitleButtons/EstmHeaderTitleButtons";
import { setActiveRow } from "slices/adv_search";
import { clearClickedTableCtrlRows, setCurrentEstmDocId, setCurrentPage } from "slices/ribbon";
import {
	applyAdvSearchSelected,
	clearSumEstmData,
	getInputXml,
	getPrintXlsx,
	getPrintXlsxWithErrors,
	getSumEstimateTree,
	setFetchStatus,
	setTableData,
} from "slices/summary_estimate";

import { RecourseCalcPosition, ScrollLocEstmToPos } from "../components/AdvSearchBox/apply_adv_search";
import { PriceLevelSwitcher, SummaryEstmFooterSignatures } from "./components";
import SummaryEstimateTableColumns from "./SummaryEstimateTableColumns";

const JustificationUnionModalBox = lazy(() => import("pages/EstimateViewer/components/JustificationModalBox/JustificationUnionModalBox"));

export const SummaryEstimateViewer = (props) => {
	const dispatch = useDispatch();

	const headerData = useSelector((state) => state.summaryEstimate.headerData);
	const rowsData = useSelector((state) => state.summaryEstimate.tableData);
	const projectTreeData = useSelector((state) => state.projectWork.tree);
	const fetchStatus = useSelector((state) => state.summaryEstimate.fetchStatus);
	const fetchStatusText = useSelector((state) => state.summaryEstimate.fetchStatusText);

	const [fullVisible, setFullVisible] = useState(true);
	const [priceLevel, setPriceLevel] = useState("current");

	const columnsData = SummaryEstimateTableColumns({ headerData, priceLevel, setPriceLevel });

	const headBlock = useRef();
	const headScrollControllBlock = useRef();
	const headBlock1 = useRef();
	const headBlock3 = useRef();
	const { id } = useParams();
	const currentPage = useSelector((state) => state.ribbon.currentPage);
	const advSearchActiveColorRow = useSelector((state) => state.advSearch.activeColorRow);
	const advSearchActiveRow = useSelector((state) => state.advSearch.activeRow);
	const [justBoxVisible, setJustBoxVisible] = useState(false);
	const [justData, setJustData] = useState(null);

	useLayoutEffect(() => {
		dispatch(setCurrentPage("SummaryEstimateViewer"));
		dispatch(setCurrentEstmDocId(id));
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		return () => {
			dispatch(clearClickedTableCtrlRows());
			dispatch(clearSumEstmData([]));
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		dispatch(getSumEstimateTree({ id, priceLevel })).then(() => {
			dispatch(applyAdvSearchSelected());
		});
	}, [priceLevel, id]); // eslint-disable-line react-hooks/exhaustive-deps

	const setHeadBlockMaxHeight = (val) => {
		for (let headBlock of [headBlock1, headBlock3]) {
			if (headBlock.current != null) headBlock.current.style.maxHeight = val;
		}
	};

	const handleSetFullVisible = () => {
		if (fullVisible === true) {
			setHeadBlockMaxHeight(0);
			setFullVisible(false);
		} else if (fullVisible === false) {
			setHeadBlockMaxHeight("100px");
			setFullVisible(true);
		}
	};

	const getPrintXlsxSummary = () => {
		dispatch(getPrintXlsx({ projectId: id, priceLevel }));
	};

	const getPrintXlsxWithErrorsSummary = () => {
		dispatch(getPrintXlsxWithErrors({ projectId: id, priceLevel }));
	};

	const getInputXmlSummary = () => {
		dispatch(getInputXml({ projectId: id, priceLevel }));
	};

	useEffect(() => {
		if (currentPage !== "SummaryEstimateViewer") return;

		if (rowsData?.length && Object.keys(advSearchActiveRow).length && props.scrollBlock["current"] != null) {
			const { rowCnt, isFound } = RecourseCalcPosition(rowsData, advSearchActiveRow, {
				rowCnt: 0,
				isFound: false,
			});
			if (isFound) {
				ScrollLocEstmToPos(props.scrollBlock, rowCnt, 310);
				setActiveRow({});
			}
		}
	}, [advSearchActiveRow, rowsData, props.scrollBlock, advSearchActiveColorRow]); // eslint-disable-line react-hooks/exhaustive-deps

	props.lowerCallbacks.current.summary["getPrintXlsxSummary"] = getPrintXlsxSummary;
	props.lowerCallbacks.current.summary["getInputXmlSummary"] = getInputXmlSummary;
	props.lowerCallbacks.current.summary["getPrintXlsxWithErrorsSummary"] = getPrintXlsxWithErrorsSummary;

	const showErrorJustification = (row) => {
		dispatch(setFetchStatus("loading"));
		apiSummaryEstimate
			.getErrorJustification(row.id)
			.then((response) => {
				setJustData(response.data);
				setJustBoxVisible(true);
				dispatch(setFetchStatus("success"));
			})
			.catch((error) => {
				dispatch(setFetchStatus("failed"));
			});
	};
	props.lowerCallbacks.current.summary["showErrorJustification"] = showErrorJustification;

	if (rowsData == null) {
		if (projectTreeData.find((x) => x._children && x._children.length && x._children.length > 0)) {
			return (
				<>
					<div className="NoDataPriceLevel">
						<PriceLevelSwitcher
							priceLevel={priceLevel}
							setPriceLevel={setPriceLevel}
							priceLevelText="Составлен в"
							baseText="базисном"
							curText="текущем"
						/>
					</div>
					<NoDataBox text="сводном сметном расчете" />
				</>
			);
		} else {
			return (
				<>
					<div className="NoDataPriceLevel">
						<PriceLevelSwitcher
							priceLevel={priceLevel}
							setPriceLevel={setPriceLevel}
							priceLevelText="Составлен в"
							baseText="базисном"
							curText="текущем"
						/>
					</div>
					<StartWorkBox />
				</>
			);
		}
	} else if (rowsData.length > 0) {
		return (
			<div className="TableOuterBox SsrTable" type="SummaryEstimate">
				<div className="TableOuterHead" ref={headBlock}>
					<div className="OuterHeadHidable" ref={headBlock1}>
						<div>
							<LabelNormWithUnder norm="Заказчик" under={headerData.customer} />
							<LabelNormWithUnder norm="Утвержден" under={headerData.approved + " г."} />
							<LabelNormWithUnder norm="Сводный сметный расчет сметной стоимостью" under={headerData.full_total_price + " тыс. руб."} />
							<LabelNormWithUnder norm="Ссылка на документ об утверждении" under="" />
						</div>
					</div>
					<div className="TableOuterHeadMainTitle">
						<div className="HeadFlex">
							<div className="TitleLeft"></div>
							<div className="TitleCenter">
								<span>СВОДНЫЙ СМЕТНЫЙ РАСЧЕТ СТОИМОСТИ СТРОИТЕЛЬСТВА № {headerData.num}</span>
								<HeaderVisibleButton fullVisible={fullVisible} f_handleVisible={handleSetFullVisible} />
							</div>
							<div className="TitleRight">
								<EstmHeaderTitleButtons data={headerData} id={id} estm_type="ssr" />
							</div>
						</div>
					</div>
					<div className="OuterHeadHidable" ref={headBlock3}>
						<div>
							<LabelTwoVerticalLevel main={headerData["name"]} second="(наименование стройки)" />
							<div className="TableOuterHeadFlex PriceLevelSelector" ref={headScrollControllBlock}>
								<PriceLevelSwitcher
									priceLevel={priceLevel}
									setPriceLevel={setPriceLevel}
									priceLevelText="Составлен в"
									baseText="базисном"
									curText="текущем"
								/>
							</div>
						</div>
					</div>
				</div>

				<Table
					rowData={rowsData}
					columnsData={columnsData}
					scrollBlock={props.scrollBlock}
					options={{
						selectOptions: {
							controlled: true,
							uncontrolled: false,
							multiple: false,
							expandOnSelect: {
								columnsName: ["name", "code"],
								fontSize: "12px",
								tableClass: "SsrTable",
							},
							aggrFooterCellSum: {
								columnsName: ["building", "mounting", "equipment", "other", "total"],
							},
						},
						displayOptions: {
							displayScrollDelay: 30,
							additinalScrollHeight: 200,
							newResizableColumns: true,
							headBlock: headBlock,
							advSeachActiveRow: advSearchActiveColorRow,
							localStoragePageKey: "SsrViewer",
							columnsWithMinWidth: [{ name: "name", minWidth: "350px" }],
						},
						tableType: "ssr",
					}}
					funcs={{
						setTableData: ({ newTableData }) => dispatch(setTableData(newTableData)),
						customFuncs: {
							showErrorJustification,
						},
					}}
				/>
				<div className="TableHeaderBlankPlaceHolder"></div>
				{rowsData != null && rowsData.length ? <SummaryEstmFooterSignatures data={headerData} /> : <></>}
				{justBoxVisible && (
					<Suspense>
						<JustificationUnionModalBox justData={justData} xmlData={null} estmId={headerData.id} f_visible={setJustBoxVisible} />
					</Suspense>
				)}
				<AlertLoading fetchStatus={fetchStatus} fetchStatusText={fetchStatusText} func={setFetchStatus} />
				<br></br>
			</div>
		);
	}
};
